// frontend/src/components/newui/NewTeamInviteModal.jsx
import React, { useState } from 'react';
import { X, Search, UserPlus } from 'lucide-react';
import axios from '../../config/axios';
import NewUserAvatar from './NewUserAvatar';

const NewTeamInviteModal = ({ isOpen, onClose, tournamentId, team, onInvite }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [invitingId, setInvitingId] = useState(null);
    const [invitedIds, setInvitedIds] = useState([]);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    const handleSearch = async () => {
        if (!query.trim()) return;

        setSearching(true);
        setError(null);

        try {
            const response = await axios.get(`/tournaments/${tournamentId}/users/search`, {
                params: { q: query.trim() }
            });

            if (response.data.success) {
                setResults(response.data.users || []);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error al buscar usuarios');
        } finally {
            setSearching(false);
        }
    };

    const handleInvite = async (userId) => {
        setInvitingId(userId);
        const success = await onInvite(team.id, userId);
        if (success) {
            setInvitedIds(prev => [...prev, userId]);
        }
        setInvitingId(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-extrabold text-[#122436] uppercase">
                            Invitar a {team.name}
                        </h3>
                        <p className="text-xs text-gray-500 font-semibold">
                            Busca por usuario o correo
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Usuario o correo..."
                            className="flex-1 h-11 rounded-xl border-2 border-gray-200 px-4 font-semibold text-[#122436]"
                        />
                        <button
                            onClick={handleSearch}
                            disabled={searching}
                            className="h-11 px-4 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                     hover:bg-[#2A91F4] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Search className="w-4 h-4" />
                        </button>
                    </div>

                    {error && (
                        <div className="text-sm text-red-500 font-semibold">{error}</div>
                    )}

                    <div className="space-y-3">
                        {results.length === 0 && !searching && (
                            <p className="text-sm text-gray-500 font-semibold">
                                No hay resultados.
                            </p>
                        )}

                        {results.map(user => (
                            <div
                                key={user.id}
                                className="flex items-center justify-between p-3 rounded-xl border-2 border-gray-200 bg-gray-50"
                            >
                                <div className="flex items-center gap-3">
                                    <NewUserAvatar user={user} size="md" />
                                    <div>
                                        <p className="font-semibold text-[#122436]">{user.username}</p>
                                        <p className="text-xs text-gray-500">{user.email}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleInvite(user.id)}
                                    disabled={invitedIds.includes(user.id) || invitingId === user.id}
                                    className="h-9 px-3 bg-[#fce503] text-[#122436] rounded-xl font-extrabold uppercase tracking-wide
                                             hover:bg-[#fce503]/90 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                >
                                    <UserPlus className="w-4 h-4 inline-block mr-2" />
                                    {invitedIds.includes(user.id) ? 'Invitado' : 'Invitar'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewTeamInviteModal;
